import styled from "styled-components";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import React, { useCallback, useMemo } from "react";
import _ from "lodash";
import { ShowCaseComponentSwitch } from "../showcase/showcase.utils";
import smoothScroll from "../../utils/animateScroll";
import { useHomeShowcaseData } from "../../utils/hooks/useHomeShowcaseData";
import { useInputMode } from "../../utils/hooks/useInputMode";

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const ScrollingRows = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 100vh;
  width: 100%;
`;

const HomeContent = ({ showCaseData, isShowcasePage = false, contentKind }) => {
  const { ref, focusKey } = useFocusable({
    focusKey: "home_showcase",
    isFocusBoundary: true,
    focusBoundaryDirections: ["up", "down", "left"],
  });

  // Fetch private/personalized showcase data - only for home page, not showcase pages
  const homeShowcaseData = useHomeShowcaseData();
  // Skip private data on showcase pages (movie, series, music, etc.)
  const continueWatch = isShowcasePage || !homeShowcaseData.isLoggedIn ? [] : homeShowcaseData.continueWatch;
  const youMayLike = isShowcasePage || !homeShowcaseData.isLoggedIn ? [] : homeShowcaseData.youMayLike;
  const recentlyUpdated = isShowcasePage ? [] : homeShowcaseData.recentlyUpdated;

  const { isMouseFocus, isMouseFocusRef } = useInputMode();
  const onRowFocus = useCallback(
    (props) => {
      // Skip scroll adjustment when focus originated from mouse hover
      if (isMouseFocusRef.current) return;
      const { y, height } = props;

      const targetScroll = y > height - 200 ? y - 200 : 0;

      smoothScroll(ref, targetScroll, 500);
    },
    [ref, isMouseFocus]
  );

  const homeData = useMemo(() => {
    let results = [];

    Object.keys(showCaseData)?.map((key) =>
      showCaseData[key]?.map((item) => results.push(item))
    );

    // Sort by priority
    results = _.sortBy(results, (x) => x.priority);

    // Create final array with injected rows at specific positions
    // (matching Flutter TV: Continue Watch after 2nd, You May Like after 5th, Recently Updated at end)
    const finalResults = [];
    let processedCount = 0;

    for (let i = 0; i < results.length; i++) {
      finalResults.push(results[i]);
      processedCount++;

      // After 2nd item: Insert Continue Watch (if available)
      if (processedCount === 2 && continueWatch && continueWatch.length > 0) {
        finalResults.push({
          _id: "recently_updated_row",
          type: "recentlyUpdated",
          title: "به‌روز شده‌ها",
          items: recentlyUpdated,
          priority: 9999,
        });
        finalResults.push({
          _id: "continue_watch_row",
          type: "continueWatch",
          title: "ادامه تماشا",
          items: continueWatch,
          priority: -1, // Will be ignored since we're placing manually
        });
      }

      // After 5th item: Insert You May Like (if available)
      if (processedCount === 5 && youMayLike && youMayLike.length > 0) {
        finalResults.push({
          _id: "you_may_like_row",
          type: "youMayLike",
          title: "شاید دوست داشته باشید",
          items: youMayLike,
          priority: -1,
        });
      }
    }

    return finalResults;
  }, [showCaseData, continueWatch, youMayLike, recentlyUpdated]);

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentWrapper>
        <ScrollingRows ref={ref}>
          <div>
            {homeData?.map((item, index) => (
              <ShowCaseComponentSwitch
                key={"sc_" + index + '_' + item._id}
                item={item}
                onRowFocus={onRowFocus}
                parentIndex={index}
                contentKind={contentKind}
              />
            ))}
          </div>
        </ScrollingRows>
      </ContentWrapper>
    </FocusContext.Provider>
  );
};

export default HomeContent;

